import type { SamplePaymentTreatment } from '@codezo/graphql';
import { Radio } from './ui/radio';
import { TREATMENT_DESCRIPTION, TREATMENT_LABEL, TREATMENT_OPTIONS } from '../lib/sample-treatment';
import { cn } from '../lib/utils';

interface SampleTreatmentPickerProps {
	value: SamplePaymentTreatment;
	onChange: (value: SamplePaymentTreatment) => void;
	/** Omit for an unlabelled group, e.g. when the dialog title already says it. */
	label?: string;
}

/**
 * The one control for choosing how a sample is paid for.
 *
 * A picker rather than toggles: the three treatments are exclusive by
 * construction now, which is the point of the enum. The pair of booleans it
 * replaced could express "prepaid AND free", a combination that meant nothing and
 * shipped a bug — a waived sample still rendering "deducted from your order".
 *
 * Used by offer authoring, the New offer wizard, the greenlight modal and the
 * order-side action, so the four surfaces can't word the same three options
 * differently.
 */
export function SampleTreatmentPicker({ value, onChange, label }: SampleTreatmentPickerProps) {
	return (
		<div className="flex flex-col gap-1">
			{label && <div className="text-sm font-medium text-text-1">{label}</div>}
			<div className="overflow-hidden rounded-lg border border-line-2">
				{TREATMENT_OPTIONS.map((option, i) => (
					<button
						key={option}
						type="button"
						onClick={() => onChange(option)}
						className={cn(
							'flex w-full cursor-pointer items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-surface-hover',
							i > 0 && 'border-t border-line-2'
						)}
						style={{
							background: value === option ? 'var(--accent-soft)' : undefined,
						}}
					>
						<span className="mt-0.5">
							<Radio checked={value === option} />
						</span>
						<span className="flex flex-col">
							<span className="text-sm text-text-1">{TREATMENT_LABEL[option]}</span>
							<span className="text-xs text-text-4">
								{TREATMENT_DESCRIPTION[option]}
							</span>
						</span>
					</button>
				))}
			</div>
		</div>
	);
}
